import { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import type { FamilyAccountView, FamilyAccountsView, SupportedLocale } from '@stm/contracts';
import { translate } from '@stm/i18n';
import { apiRequest } from './api';
import { formatMoney } from './billing';
import { BillingStatement } from './BillingStatement';
import { errorKeyOf, type ErrorKey } from './BillingForms';

export interface PaymentsPanelProps {
  locale: SupportedLocale;
  organizationId: string;
}

const noRefresh = 'family-view';

/**
 * The family view of the dues ledger: only the accounts the club approved for this person, each
 * with its own position and statement, never summed across accounts. It only shows information.
 */
export function PaymentsPanel({ locale, organizationId }: PaymentsPanelProps): React.JSX.Element {
  const base = `/api/v1/organizations/${organizationId}/payments`;
  const [accounts, setAccounts] = useState<FamilyAccountView[] | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [openAccountId, setOpenAccountId] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    setAccounts(null);
    setErrorKey(null);
    setOpenAccountId(null);
    apiRequest<FamilyAccountsView>(`${base}/accounts`)
      .then((loaded) => {
        if (current) setAccounts(loaded.items);
      })
      .catch((error: unknown) => {
        if (current) setErrorKey(errorKeyOf(error));
      });
    return () => {
      current = false;
    };
  }, [base]);

  return (
    <Box component="section" aria-labelledby="payments-title" sx={{ mb: 3 }}>
      <Typography variant="h6" component="h2" id="payments-title" gutterBottom>
        {translate(locale, 'payments.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate(locale, 'payments.note')}
      </Typography>
      {errorKey !== null && <Alert severity="error">{translate(locale, errorKey)}</Alert>}
      {accounts === null && errorKey === null && (
        <Typography>{translate(locale, 'payments.loading')}</Typography>
      )}
      {accounts?.length === 0 && <Typography>{translate(locale, 'payments.empty')}</Typography>}
      <Stack component="ul" spacing={2} sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {accounts?.map((account) => {
          const open = openAccountId === account.id;
          return (
            <Box
              component="li"
              key={account.id}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, padding: 2 }}
            >
              <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" component="h3">
                  {account.label}
                </Typography>
                {account.status === 'closed' && (
                  <Chip size="small" label={translate(locale, 'billing.accounts.closed')} />
                )}
              </Stack>
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 3, mb: 1 }}>
                {(
                  [
                    ['billing.position.balance', account.position.balanceMinor],
                    ['billing.position.overdue', account.position.overdueMinor],
                    ['billing.position.upcoming', account.position.upcomingMinor],
                    ['billing.position.credit', account.position.unallocatedCreditMinor],
                  ] as const
                ).map(([labelKey, amountMinor]) => (
                  <Box key={labelKey}>
                    <Typography variant="body2" color="text.secondary">
                      {translate(locale, labelKey)}
                    </Typography>
                    <Typography component="p" sx={{ fontWeight: 600 }}>
                      {formatMoney(amountMinor, account.currency, locale)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
              <Button
                size="small"
                aria-expanded={open}
                onClick={() => {
                  setOpenAccountId(open ? null : account.id);
                }}
              >
                {translate(locale, open ? 'payments.statement.hide' : 'payments.statement.show')}
              </Button>
              {open && (
                <Box sx={{ mt: 2 }}>
                  <BillingStatement
                    locale={locale}
                    base={base}
                    accountId={account.id}
                    refreshToken={noRefresh}
                  />
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
